import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  listProductImportTemplates,
  listAmazonImportTemplates,
  uploadProductImportTemplate,
  uploadAmazonImportTemplate,
  activateProductImportTemplate,
  downloadActiveTemplateToDisk,
  deleteProductImportTemplate,
} from '../utils/productImportTemplateService';
import './ImportTemplateManager.css';

const emptyUploadState = () => ({ name: '', file: null, uploading: false });

const TemplateSection = ({
  title,
  description,
  hint,
  accentClass,
  templates,
  loading,
  uploadState,
  setUploadState,
  activatingId,
  deletingId,
  onUpload,
  onActivate,
  onDownloadActive,
  onDelete,
  onRefresh,
}) => {
  const activeRow = templates.find((t) => t.is_active);

  return (
    <div className={`itm-section ${accentClass}`}>
      <div className="itm-section-header">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className="itm-upload-card">
        <h3>Upload new template</h3>
        <form onSubmit={onUpload}>
          <div className="itm-upload-row">
            <label>
              Display name
              <input
                type="text"
                value={uploadState.name}
                onChange={(e) => setUploadState((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Amazon import 2025"
              />
            </label>
            <label>
              File (.xlsx or .csv)
              <input
                type="file"
                accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => setUploadState((s) => ({ ...s, file: e.target.files?.[0] || null }))}
              />
            </label>
            <button type="submit" className="itm-btn" disabled={uploadState.uploading}>
              {uploadState.uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
        <p className="itm-hint">{hint}</p>
      </div>

      <div className="itm-toolbar">
        <button type="button" className="itm-btn itm-btn-secondary" onClick={onRefresh} disabled={loading}>
          Refresh list
        </button>
        {activeRow && (
          <button type="button" className="itm-btn" onClick={onDownloadActive}>
            Download active template (preview)
          </button>
        )}
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="itm-table-wrap">
          <table className="itm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={5}>No templates yet. Upload one above.</td>
                </tr>
              ) : (
                templates.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.file_type?.toUpperCase()}</td>
                    <td>
                      {row.is_active ? <span className="itm-badge">Active</span> : '—'}
                    </td>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td>
                      <div className="itm-actions">
                        {!row.is_active && (
                          <button
                            type="button"
                            onClick={() => onActivate(row.id)}
                            disabled={activatingId === row.id}
                          >
                            {activatingId === row.id ? 'Activating…' : 'Activate'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="danger"
                          onClick={() => onDelete(row)}
                          disabled={deletingId === row.id}
                        >
                          {deletingId === row.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ImportTemplateManager = () => {
  const [productsTemplates, setProductsTemplates] = useState([]);
  const [amazonTemplates, setAmazonTemplates] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [amazonLoading, setAmazonLoading] = useState(true);

  const [productsUpload, setProductsUpload] = useState(emptyUploadState());
  const [amazonUpload, setAmazonUpload] = useState(emptyUploadState());

  const [productsActivatingId, setProductsActivatingId] = useState(null);
  const [amazonActivatingId, setAmazonActivatingId] = useState(null);
  const [productsDeletingId, setProductsDeletingId] = useState(null);
  const [amazonDeletingId, setAmazonDeletingId] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 5000);
  };

  const loadProductsTemplates = useCallback(async () => {
    setProductsLoading(true);
    const { data, error: e } = await listProductImportTemplates('products');
    if (e) { setError(e.message); setProductsTemplates([]); }
    else setProductsTemplates(data);
    setProductsLoading(false);
  }, []);

  const loadAmazonTemplates = useCallback(async () => {
    setAmazonLoading(true);
    const { data, error: e } = await listAmazonImportTemplates();
    if (e) { setError(e.message); setAmazonTemplates([]); }
    else setAmazonTemplates(data);
    setAmazonLoading(false);
  }, []);

  useEffect(() => {
    loadProductsTemplates();
    loadAmazonTemplates();
  }, [loadProductsTemplates, loadAmazonTemplates]);

  const handleProductsUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!productsUpload.file) { setError('Choose a .csv or .xlsx file.'); return; }
    setProductsUpload((s) => ({ ...s, uploading: true }));
    const result = await uploadProductImportTemplate(productsUpload.file, productsUpload.name, 'products');
    setProductsUpload((s) => ({ ...s, uploading: false }));
    if (!result.success) { setError(result.error || 'Upload failed'); return; }
    setProductsUpload(emptyUploadState());
    showSuccess('Template uploaded. Activate it to make it available to clients.');
    await loadProductsTemplates();
  };

  const handleAmazonUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!amazonUpload.file) { setError('Choose a .csv or .xlsx file.'); return; }
    setAmazonUpload((s) => ({ ...s, uploading: true }));
    const result = await uploadAmazonImportTemplate(amazonUpload.file, amazonUpload.name);
    setAmazonUpload((s) => ({ ...s, uploading: false }));
    if (!result.success) { setError(result.error || 'Upload failed'); return; }
    setAmazonUpload(emptyUploadState());
    showSuccess('Amazon template uploaded. Activate it to make it available to clients.');
    await loadAmazonTemplates();
  };

  const handleProductsActivate = async (id) => {
    setError('');
    setProductsActivatingId(id);
    const result = await activateProductImportTemplate(id);
    setProductsActivatingId(null);
    if (!result.success) { setError(result.error || 'Activation failed'); return; }
    showSuccess('Active template updated.');
    await loadProductsTemplates();
  };

  const handleAmazonActivate = async (id) => {
    setError('');
    setAmazonActivatingId(id);
    const result = await activateProductImportTemplate(id);
    setAmazonActivatingId(null);
    if (!result.success) { setError(result.error || 'Activation failed'); return; }
    showSuccess('Active Amazon template updated.');
    await loadAmazonTemplates();
  };

  const handleProductsDownload = async () => {
    setError('');
    const result = await downloadActiveTemplateToDisk('products');
    if (!result.success) { setError(result.error || 'Download failed'); return; }
    showSuccess(`Downloaded ${result.filename || 'template'}`);
  };

  const handleAmazonDownload = async () => {
    setError('');
    const result = await downloadActiveTemplateToDisk('amazon_products');
    if (!result.success) { setError(result.error || 'Download failed'); return; }
    showSuccess(`Downloaded ${result.filename || 'template'}`);
  };

  const handleProductsDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    setError('');
    setProductsDeletingId(row.id);
    const result = await deleteProductImportTemplate(row.id, row.file_path);
    setProductsDeletingId(null);
    if (!result.success) { setError(result.error || 'Delete failed'); return; }
    showSuccess('Template removed.');
    await loadProductsTemplates();
  };

  const handleAmazonDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    setError('');
    setAmazonDeletingId(row.id);
    const result = await deleteProductImportTemplate(row.id, row.file_path);
    setAmazonDeletingId(null);
    if (!result.success) { setError(result.error || 'Delete failed'); return; }
    showSuccess('Template removed.');
    await loadAmazonTemplates();
  };

  return (
    <Layout>
      <div className="itm-page">
        <div className="itm-page-header">
          <h1>Import templates</h1>
          <p>
            Manage spreadsheet templates for both product types. Only one template per type can be active at a time.
            Clients download the active template when importing products via spreadsheet.
          </p>
        </div>

        {error && <div className="itm-alert itm-alert--error">{error}</div>}
        {success && <div className="itm-alert itm-alert--success">{success}</div>}

        <TemplateSection
          title="Products"
          description="Templates used when clients import regular products via Spreadsheet import."
          hint='Keep the first row as column headers (e.g. sku_number, product_name). After upload, click Activate on the row you want clients to use.'
          accentClass="itm-section--products"
          templates={productsTemplates}
          loading={productsLoading}
          uploadState={productsUpload}
          setUploadState={setProductsUpload}
          activatingId={productsActivatingId}
          deletingId={productsDeletingId}
          onUpload={handleProductsUpload}
          onActivate={handleProductsActivate}
          onDownloadActive={handleProductsDownload}
          onDelete={handleProductsDelete}
          onRefresh={loadProductsTemplates}
        />

        <TemplateSection
          title="Amazon Products"
          description="Templates used when clients import Amazon products via Spreadsheet import. Note: image and file fields must be added manually after import."
          hint='Required columns: sku, product_name, brand_name, product_category. Fulfillment method: FBA or FBM. Boolean fields: true or false.'
          accentClass="itm-section--amazon"
          templates={amazonTemplates}
          loading={amazonLoading}
          uploadState={amazonUpload}
          setUploadState={setAmazonUpload}
          activatingId={amazonActivatingId}
          deletingId={amazonDeletingId}
          onUpload={handleAmazonUpload}
          onActivate={handleAmazonActivate}
          onDownloadActive={handleAmazonDownload}
          onDelete={handleAmazonDelete}
          onRefresh={loadAmazonTemplates}
        />
      </div>
    </Layout>
  );
};

export default ImportTemplateManager;
